/**
 * @module modules/graph-list-sk
 * @description <h2><code>graph-list-sk</code></h2>
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "GraphListSk", {
    enumerable: true,
    get: function() {
        return GraphListSk;
    }
});
const _lit = require("lit");
const _decorators = require("lit/decorators.js");
const _define = require("../../../elements-sk/modules/define");
require("../../../elements-sk/modules/spinner-sk");
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
let GraphListSk = class GraphListSk extends _lit.LitElement {
    updated(changedProperties) {
        super.updated(changedProperties);
        if (changedProperties.has('items')) {
            // Learn and register the order of any new items provided by the parent.
            this.items.forEach((item)=>{
                if (!this._masterOrder.includes(item.id)) {
                    this._masterOrder.push(item.id);
                }
            });
            this.reconcile();
        }
    }
    createRenderRoot() {
        return this;
    }
    render() {
        return (0, _lit.html)`
      ${this.renderControls()}
      <div
        id="graph-container"
        @x-axis-toggled=${this.syncXAxisLabel}
        @range-changing-in-multi=${this.syncExtendRangeOnSummaryBar}
        @selection-range-changed=${this.syncChartSelection}
        @even-x-axis-spacing-changed=${this.syncEvenXAxisSpacing}></div>
      ${this.renderControls()}
    `;
    }
    renderControls() {
        return (0, _lit.html)`
      <div class="controls">
        <div class="status-info">
          ${this._renderedCount > 0 && this._renderedCount < this.items.length ? (0, _lit.html)`<span>Showing ${this._renderedCount} of ${this.items.length} graphs.</span>` : ''}
          ${this._currentlyLoading ? (0, _lit.html)`
                <span class="loading-status">
                  <spinner-sk id="loading-spinner" active></spinner-sk>
                  <span class="loading-message">${this._currentlyLoading}</span>
                </span>
              ` : ''}
        </div>
        ${this._renderedCount < this.items.length && !this._loadAllPending && this._loadingTarget < this.items.length && !this._paginationDisabled ? (0, _lit.html)`
              <div class="load-buttons">
                <button
                  @click=${()=>this.loadMore(false)}
                  ?disabled=${this._currentlyLoading !== ''}>
                  Load next ${Math.min(this.visibleLimit, this.items.length - this._renderedCount)}
                </button>
                <button @click=${this.triggerLoadAll}>Load All</button>
              </div>
            ` : ''}
      </div>
    `;
    }
    async triggerLoadAll() {
        if (this._currentlyLoading) {
            this._loadAllPending = true;
        } else {
            await this.loadMore(true);
        }
    }
    async reload() {
        if (this._graphDiv) {
            this._graphDiv.replaceChildren();
        }
        this._graphCache.clear();
        this._loadedGraphIds.clear();
        this._renderedCount = 0;
        this._allGraphsLoaded = false;
        this._paginationDisabled = false;
        await this.updateComplete;
        await this.loadMore();
    }
    async reconcile() {
        await this.updateComplete;
        if (!this._graphDiv) return;
        // Prune cached elements that are no longer present in the updated items array.
        const newItemsSet = new Set(this.items.map((i)=>i.id));
        for (const [id, el] of this._graphCache){
            if (!newItemsSet.has(id)) {
                el.remove();
            }
        }
        // Resetting the count ensures that loadMore() iterates through the entire
        // current list from the beginning.
        // Allows sequential appendChild operations to naturally sort the physical DOM.
        this._renderedCount = 0;
        this._allGraphsLoaded = false;
        await this.loadMore();
    }
    getElementForId(id) {
        return this._graphCache.get(id);
    }
    addGraph(item) {
        if (this.items.some((i)=>i.id === item.id)) return;
        // Register the item if it has never been seen before
        if (!this._masterOrder.includes(item.id)) {
            this._masterOrder.push(item.id);
        }
        // Sort the updated array based on the historical master order to guarantee original positioning
        const newItems = [
            ...this.items,
            item
        ];
        newItems.sort((a, b)=>this._masterOrder.indexOf(a.id) - this._masterOrder.indexOf(b.id));
        this.items = newItems;
    }
    removeGraph(id) {
        this.items = this.items.filter((item)=>item.id !== id);
    }
    async loadMore(loadAll = false) {
        if (this._currentlyLoading) return;
        await this.updateComplete;
        if (!this._graphDiv) return;
        if (loadAll) {
            this._paginationDisabled = true;
        }
        const targetCount = this._paginationDisabled ? this.items.length : Math.min(this._renderedCount + this.visibleLimit, this.items.length);
        if (this._renderedCount >= targetCount) {
            this.dispatchEvent(new CustomEvent('graphs-loaded', {
                bubbles: true
            }));
            return;
        }
        this._loadingTarget = targetCount;
        const itemsToLoad = this.items.slice(this._renderedCount, targetCount);
        let loadedCount = 0;
        this._allGraphsLoaded = false;
        const currentItemsRef = this.items;
        for(let i = 0; i < itemsToLoad.length; i += this.chunkSize){
            // Abort gracefully if the items array is mutated by a concurrent user action.
            if (this.items !== currentItemsRef) return;
            this._currentlyLoading = `Loading (${this._renderedCount + loadedCount}/${targetCount})... [out of ${this.items.length} total]`;
            const chunk = itemsToLoad.slice(i, i + this.chunkSize);
            chunk.forEach((item)=>{
                const id = item.id;
                var _this__graphCache_get;
                let graphElement = (_this__graphCache_get = this._graphCache.get(id)) !== null && _this__graphCache_get !== void 0 ? _this__graphCache_get : null;
                if (!graphElement) {
                    graphElement = item.generateGraph();
                    if (graphElement) {
                        this._graphCache.set(id, graphElement);
                    }
                }
                if (graphElement) {
                    // Calling appendChild ensures the node is moved to the end of the container,
                    // matching the array's exact sorted order.
                    this._graphDiv.appendChild(graphElement);
                    const height = this.items.length === 1 ? '500px' : '250px';
                    graphElement.updateChartHeight(height);
                }
            });
            const promises = chunk.map((item)=>new Promise((resolve)=>{
                    const id = item.id;
                    const graphElement = this._graphCache.get(id);
                    if (!graphElement || this._loadedGraphIds.has(id)) {
                        loadedCount++;
                        resolve();
                        return;
                    }
                    const listener = ()=>{
                        graphElement.removeEventListener('data-loaded', listener);
                        this._loadedGraphIds.add(id);
                        loadedCount++;
                        resolve();
                    };
                    graphElement.addEventListener('data-loaded', listener);
                    // Failsafe: Releasing the lock if data-loaded is not dispatched,
                    // preventing the UI from hanging indefinitely on complex queries.
                    setTimeout(()=>{
                        graphElement.removeEventListener('data-loaded', listener);
                        this._loadedGraphIds.add(id);
                        resolve();
                    }, 60000);
                }));
            await Promise.all(promises);
            this.updateChartHeights();
        }
        this._renderedCount = targetCount;
        this._loadingTarget = 0;
        this._currentlyLoading = '';
        // Lock expansion open permanently if all available items have been rendered.
        if (this._renderedCount >= this.items.length) {
            this._allGraphsLoaded = true;
            this._paginationDisabled = true;
        }
        // Process a 'Load All' command if clicked while a chunk was actively resolving.
        if (this._loadAllPending) {
            this._loadAllPending = false;
            await this.loadMore(true);
        }
        this.dispatchEvent(new CustomEvent('graphs-loaded', {
            bubbles: true
        }));
        const graphs = this.getGraphs();
        if (graphs.length > 0) {
            graphs[0].broadcastSelection();
        }
    }
    getGraphs() {
        if (!this._graphDiv) return [];
        return Array.from(this._graphDiv.querySelectorAll('explore-simple-sk'));
    }
    updateChartHeights() {
        const graphs = this.getGraphs();
        graphs.forEach((graph)=>{
            const height = graphs.length === 1 ? '500px' : '250px';
            graph.updateChartHeight(height);
        });
    }
    // --- Synchronization Handlers ---
    async syncExtendRangeOnSummaryBar(e) {
        const graphs = this.getGraphs();
        const offset = e.detail.offsetInSeconds;
        const range = e.detail.value;
        graphs.forEach(async (graph)=>{
            await graph.extendRange(range, offset);
        });
    }
    async syncChartSelection(e) {
        const graphs = this.getGraphs();
        if (!e.detail.value) return;
        if (graphs.length > 1 && e.detail.offsetInSeconds !== undefined) {
            await graphs[0].extendRange(e.detail.value, e.detail.offsetInSeconds);
        }
        graphs.forEach((graph, i)=>{
            if (i !== e.detail.graphNumber && e.detail.offsetInSeconds === undefined) {
                var _e_detail_start, _e_detail_end;
                graph.updateSelectedRangeWithPlotSummary(e.detail.value, (_e_detail_start = e.detail.start) !== null && _e_detail_start !== void 0 ? _e_detail_start : 0, (_e_detail_end = e.detail.end) !== null && _e_detail_end !== void 0 ? _e_detail_end : 0);
            }
        });
        graphs.forEach(async (graph, i)=>{
            if (i !== e.detail.graphNumber && e.detail.offsetInSeconds !== undefined) {
                await graph.requestComplete;
                var _e_detail_start, _e_detail_end;
                graph.updateSelectedRangeWithPlotSummary(e.detail.value, (_e_detail_start = e.detail.start) !== null && _e_detail_start !== void 0 ? _e_detail_start : 0, (_e_detail_end = e.detail.end) !== null && _e_detail_end !== void 0 ? _e_detail_end : 0);
            }
        });
    }
    syncXAxisLabel(e) {
        const graphs = this.getGraphs();
        graphs.forEach((graph)=>graph.switchXAxis(e.detail));
    }
    syncEvenXAxisSpacing(e) {
        const newValue = e.detail.value;
        const graphs = this.getGraphs();
        graphs.forEach((graphNode)=>{
            const graph = graphNode;
            if (graph.state.graph_index !== e.detail.graph_index) {
                graph.state.evenXAxisSpacing = newValue;
                graph.setUseDiscreteAxis(newValue);
                graph.render();
            }
        });
    }
    constructor(...args){
        super(...args);
        this.items = [];
        this.chunkSize = 5;
        this.visibleLimit = 25;
        this._renderedCount = 0;
        this._currentlyLoading = '';
        this._allGraphsLoaded = true;
        this._loadAllPending = false;
        this._loadingTarget = 0;
        // Permanently bypasses chunked loading limits once the full list has been requested or rendered.
        this._paginationDisabled = false;
        // Caches generated graph elements to prevent redundant network requests and iframe reflows.
        this._graphCache = new Map();
        // Tracks which graphs have successfully resolved their initial 'data-loaded' event.
        this._loadedGraphIds = new Set();
        // Registry to record the original sequence of items provided by the parent.
        // Used to restore an item's correct historical position if it is removed and later re-added.
        this._masterOrder = [];
    }
};
_ts_decorate([
    (0, _decorators.property)({
        attribute: false
    }),
    _ts_metadata("design:type", Array)
], GraphListSk.prototype, "items", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Number
    }),
    _ts_metadata("design:type", Number)
], GraphListSk.prototype, "chunkSize", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Number
    }),
    _ts_metadata("design:type", Number)
], GraphListSk.prototype, "visibleLimit", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Number)
], GraphListSk.prototype, "_renderedCount", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", String)
], GraphListSk.prototype, "_currentlyLoading", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Boolean)
], GraphListSk.prototype, "_allGraphsLoaded", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Boolean)
], GraphListSk.prototype, "_loadAllPending", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Number)
], GraphListSk.prototype, "_loadingTarget", void 0);
_ts_decorate([
    (0, _decorators.state)(),
    _ts_metadata("design:type", Boolean)
], GraphListSk.prototype, "_paginationDisabled", void 0);
_ts_decorate([
    (0, _decorators.query)('#graph-container'),
    _ts_metadata("design:type", typeof HTMLDivElement === "undefined" ? Object : HTMLDivElement)
], GraphListSk.prototype, "_graphDiv", void 0);
(0, _define.define)('graph-list-sk', GraphListSk);
